import axios from 'axios';
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';
import { toast } from 'react-toastify';
import CircleLoader from '../components/common/circleLoader';
import settings from '../constants/constants';
import { getUser, isLoggedIn, logout } from '../services/authentication.service';
import { withFeatureFlags } from '@harnessio/ff-react-client-sdk';

const SignUpForm = lazy(() => import('../components/auth/signupForm'));
const SignInForm = lazy(() => import('../components/auth/signInForm'));
const ForgotPassword = lazy(() => import('../components/auth/forgotPassword'));
const ResetPassword = lazy(() => import('../components/auth/resetPassword'));
const Signout = lazy(() => import('../components/auth/signout'));
const TermsAndPolicy = lazy(() => import('../components/auth/termsAndPolicy'));
const VerifyUserByAdmin = lazy(() => import('../components/auth/verifyUserByAdmin'));
const ProductCatalog = lazy(() => import('../pages/products/productCatalog'));
const ProductOffering = lazy(() => import('../pages/products/productOffering'));
const Order = lazy(() => import('../pages/orders/order'));
const OrderDetail = lazy(() => import('../pages/orders/orderDetail'));
const ViewCart = lazy(() => import('../pages/cart/viewCart'));
const HealthCheck = lazy(() => import('./healthCheck'));
const Inventory = lazy(() => import('../components/inventory/product'));
const HeaderUnauth = lazy(() => import('../components/layout/header-unauth'));
const OrderSuccess = lazy(() => import('../pages/orders/orderSuccess'));
const ConsignmentDetail = lazy(() => import('../pages/orders/consignmentDetail'));
const AuthLayout = lazy(() => import('../components/layout/Authlayout'));
const PharmacyUsers = lazy(() => import('../components/users/users'));
const Products = lazy(() => import('../pages/s8form/product'));
const Success = lazy(() => import('../pages/s8form/success'));
const Suppliers = lazy(() => import('../pages/suppliers/supplierUsers'));
const CartLoader = lazy(() => import('../pages/cartLoader/cartLoader'));
const AdminDashboard = lazy(() => import('../pages/admin/dashboard'));

axios.interceptors.response.use(
  response => response,
  error => {
    const status = error.response && error.response.status;
    if (status === settings.unauthorized) {
      logout(true);
    }
    return Promise.reject(error);
  }
);

const allRoles = [
  settings.role.admin,
  settings.role.pharmacist,
  settings.role.supplier
];
const pharmacistOnly = [settings.role.pharmacist];
const adminOnly = [settings.role.admin];

const AppRoutes = props => (<React.Fragment>
  <BrowserRouter>
    <Suspense fallback={<CircleLoader/>}>
      <Routes>
        <Route path="/" element={<SignInForm {...props} />} >
          <Route path="/login" element={<SignInForm {...props}/>} >
            <Route path=":loginType" element={<SignInForm {...props}/>} />
          </Route>
        </Route>
        <Route path="/feedback" element={<Products {...props}/>} />
        <Route path="/feedback/success/:isdispute" element={<Success {...props}/>} />
        <Route path="/register" element={<SignUpForm {...props}/>} />
        <Route path="/forgot-password" element={<ForgotPassword {...props}/>} >
          <Route path=":loginType" element={<ForgotPassword {...props}/>} />
        </Route>
        <Route path="reset-password" element={<ResetPassword {...props} />} />
        <Route path="/health-check" element={<HealthCheck {...props}/>} />
        <Route path="/policy" element={<TermsAndPolicy {...props}/>} />
        <Route path="/verify-pharmacist/:id" element={<VerifyUserByAdmin {...props}/>} />
        <Route path="/products" element={<AuthenticatedRoute element={ProductCatalog} role={allRoles} {...props} />} />
        <Route path="/products/:id" element={<AuthenticatedRoute element={ProductOffering} role={allRoles} {...props}/>} />
        <Route path="/logout" element={<AuthenticatedRoute element={Signout} role={allRoles} {...props}/>} />
        <Route path="/orders" element={<AuthenticatedRoute element={Order} role={allRoles} {...props} />} />
        <Route path="/orders/back-orders" element={<AuthenticatedRoute element={Order} role={allRoles} {...props} />} />
        <Route path="/orders/consignments" element={<AuthenticatedRoute element={Order} role={allRoles} {...props} />} />
        <Route path='/order-success' element={<AuthenticatedRoute element={OrderSuccess} role={pharmacistOnly} {...props} />}  />
        <Route path="/orders/:id" element={<AuthenticatedRoute element={OrderDetail} role={allRoles} {...props}/>} />
        <Route path="/consignments/:id&:from" element={<AuthenticatedRoute element={ConsignmentDetail} role={allRoles} {...props}/>} />
        <Route path="/cart" element={<AuthenticatedRoute element={ViewCart} role={pharmacistOnly} {...props}/>} />
        <Route path="/inventory" element={<AuthenticatedRoute element={Inventory} role={adminOnly} {...props}/>} />
        <Route path="/users" element={<AuthenticatedRoute element={PharmacyUsers} role={adminOnly} {...props}/>} />
        <Route path="/users/approvals" element={<AuthenticatedRoute element={PharmacyUsers} role={adminOnly} {...props}/>} />
        <Route path="/users/consignments" element={<AuthenticatedRoute element={PharmacyUsers} role={adminOnly} {...props}/>} />
        <Route path="/suppliers" element={<AuthenticatedRoute element={Suppliers} role={adminOnly} {...props} />} />
        <Route path="/cart-loader" element={<AuthenticatedRoute element={CartLoader} role={adminOnly} {...props} />} />
        <Route path="/irk90bvvw3" element={<AuthenticatedRoute element={AdminDashboard} role={adminOnly} {...props} />} />
        <Route path="/*" element={<NoMatch {...props}/>} />
      </Routes>
    </Suspense>
  </BrowserRouter>
</React.Fragment>);

const NoMatch = () => {
  return <div style={{ 'height': '100vh' }}>
    <HeaderUnauth />
    <div className="card text-center shadow py-4 center-box" style={{ maxWidth: '450px' }}>
      <div className="card-body">
        <h2 className="card-title">404</h2>
        <p className="card-text">This page was not found. You may have mistyped the address or the page may have moved.</p>
        <Link className="btn btn-primary" to='/'>Go to homepage</Link>
        <p className="mt-4">If you are still facing issues, please contact our support: <a href="mailto:support@leafio.com">support@leafio.com</a></p>
      </div>
    </div>
  </div>;
};

const AuthenticatedRoute = ({ element: Component, ...rest }) => {

  if (!isLoggedIn()) {
    toast('Please Login to continue', { toastId: 'login-first', type: 'warning' });
    return <Navigate to='/login/pharmacist' />;
  }

  const curUser = getUser().user;
  const curRole = curUser.role_id;
  if (curRole !== null && (rest.role.indexOf(curRole) === -1)) {
    return <Navigate to='/*' />;
  }

  return (
    <AuthLayout cssClassName={'main-content justify-content-center'} mainContent={<Component {...rest} />}>
    </AuthLayout>
  );
};

export default withFeatureFlags(AppRoutes);
